import { useEffect, useState } from "react";
import { motion } from "framer-motion";

export default function Hero({ onBuy }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 md:pt-32 pb-20 overflow-hidden bg-cream"
    >
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[80vh]">
        {/* Left column */}
        <div className="lg:col-span-7 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={mounted ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3 }}
            className="section-num mb-8"
            data-testid="hero-eyebrow"
          >
            Workbook · Maio 2026
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-[2.5rem] sm:text-6xl md:text-7xl xl:text-[6.5rem] text-ink mb-8"
          >
            <motion.span
              initial={{ opacity: 0, y: 25 }}
              animate={mounted ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="block"
            >
              Quando já
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 25 }}
              animate={mounted ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="block"
            >
              não dá para
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 25 }}
              animate={mounted ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="block"
            >
              continuar
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 25 }}
              animate={mounted ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="font-serif-display text-orange normal-case tracking-tight block"
              style={{ fontWeight: 400, lineHeight: 0.96, textTransform: "lowercase" }}
            >
              igual.
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={mounted ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.25 }}
            data-testid="hero-subtitle"
            className="text-lg md:text-xl text-ink-soft max-w-xl mb-10 leading-relaxed"
          >
            O guia que te faltava para deixares de adiar
            <span className="text-ink font-medium"> a conversa mais importante da tua vida</span> —
            a que tens contigo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={mounted ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-start mb-10"
          >
            <button
              onClick={onBuy}
              data-testid="hero-buy-button"
              className="btn-primary px-9 py-4 inline-flex items-center justify-center gap-3 w-full sm:w-auto"
            >
              <span>Quero o meu workbook</span>
              <span className="text-[#F5F0E8] text-xs font-bold opacity-90">29,90€</span>
            </button>
            <a
              href="#comprar"
              data-testid="hero-learn-more"
              className="btn-ghost px-9 py-4 text-center w-full sm:w-auto"
            >
              Saber mais
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={mounted ? { opacity: 1 } : {}}
            transition={{ duration: 0.4, delay: 0.35 }}
            className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-ink"
          >
            <span className="smallcaps">PDF · 45 páginas</span>
            <span className="w-1 h-1 rounded-full bg-orange/60" />
            <span className="smallcaps">10 capítulos · 10 exercícios</span>
            <span className="hidden md:inline w-1 h-1 rounded-full bg-orange/60" />
            <span className="hidden md:inline smallcaps">Entrega imediata</span>
          </motion.div>
        </div>

        {/* Right column — book cover, single focal point */}
        <div className="lg:col-span-5 relative flex items-center justify-center min-h-[500px] lg:min-h-[680px]">
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: -6, scale: 0.92 }}
            animate={mounted ? { opacity: 1, y: 0, rotate: -2, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative float"
          >
            <div
              className="relative"
              style={{ filter: "drop-shadow(0 30px 50px rgba(31,29,27,0.18))" }}
            >
              <img
                src="/workbook/p01.jpg"
                alt="Capa do workbook"
                className="w-[280px] sm:w-[340px] md:w-[400px] lg:w-[440px] h-auto border border-line"
                data-testid="hero-cover-image"
              />
            </div>
          </motion.div>

          {/* Quote removed per user request */}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={mounted ? { opacity: 1 } : {}}
        transition={{ duration: 0.45, delay: 0.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-ink-soft"
      >
        <span className="smallcaps">desliza</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-8 bg-[#C9C1B3]"
        />
      </motion.div>
    </section>
  );
}
